import React from 'react';
import BaseLayout from '../../layouts/BaseLayout';
import ErrorBoundary from '../../components/ErrorBoundary';
import IconDetail from '../../components/IconDetail';


export const runtime = 'experimental-edge';

// Loading component shown during SSR build
const LoadingComponent = () => (
  <div className="p-8 text-center">
    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
    <p className="text-xl">Loading icon details...</p>
  </div>
);

// Enhanced server-side data fetching with improved error handling
export async function getServerSideProps(context) {
  const { collection: rawCollection, slug } = context.params;
  
  // Collection mapping
  const collectionMap = {
    'basic-icons': 'basic',
    'outline-icons': 'outline',
    'polish-blue-icons': 'polish-blue',
    'basic3d': 'basic3d',
  };

  const collection = collectionMap[rawCollection] || rawCollection;
  
  // Get backend URL from environment variables with fallback
  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 
                    process.env.NEXT_PUBLIC_BACK_URL || 
                    'http://localhost:8000';
  
  // Define similar collections based on current collection
  let similarCollections = [];
  switch (collection) {
    case 'polish-blue':
      similarCollections = ['polish-blue', 'outline',  'basic3d', 'basic'];
      break;
    case 'basic':
      similarCollections = ['basic', 'outline', 'basic3d', 'polish-blue' ];
      break;
    case 'outline':
      similarCollections = ['outline', 'basic', 'basic3d', 'polish-blue' ];
      break;
    case 'basic3d':
      similarCollections = ['basic3d', 'outline', 'basic', 'polish-blue'];
      break;
    default:
      similarCollections = ['outline', 'polish-blue', 'basic3d', 'basic'];
  }
  
  // If slug contains 'outline' and collection isn't 'outline', prioritize outline
  if (slug?.includes('outline') && collection !== 'outline') {
    similarCollections = ['outline', 'basic', 'polish-blue', 'basic3d'];
  }

  // Default values for the return object
  const defaultProps = {
    slug: slug || '',
    collection: collection || 'basic',
    backendUrl,
    iconData: null,
    imageUrl: '',
    similarIconsData: { items: [] },
    similarCollections,
    authError: false,
    error: null,
    timestamp: Date.now()
  };

  try {
    // Get authentication token from cookies - try different possible cookie names
    const possibleCookieNames = ['auth_token', 'firebase_token', 'id_token', 'token'];
    let authCookie = null;
    
    // Safely access cookies
    const cookies = context.req?.cookies || {};
    
    for (const cookieName of possibleCookieNames) {
      if (cookies[cookieName]) {
        authCookie = cookies[cookieName];
        break;
      }
    }
    
    // Also check Authorization header if passed by browser
    const authHeader = context.req?.headers?.authorization;
    if (!authCookie && authHeader && authHeader.startsWith('Bearer ')) {
      authCookie = authHeader.substring(7);
    }
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization header if token exists
    if (authCookie) {
      headers['Authorization'] = `Bearer ${authCookie}`;
    }
    
    // Fetch the icon data from the API with auth if available
    let iconResponse;
    try {
      iconResponse = await fetch(`${backendUrl}/asset`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ slug: slug || '' }),
      });
    } catch (fetchError) {
      return {
        props: {
          ...defaultProps,
          error: `Failed to connect to server: ${fetchError.message}`
        }
      };
    }
    
    let iconData = null;
    let authError = false;
    let serverErrorMessage = ''; 
    
    if (iconResponse.status === 401 || iconResponse.status === 403) {
      // Auth error, but we should still render the page with limited functionality
      authError = true;
      
      // Get error message from response
      try {
        const errorData = await iconResponse.json();
        serverErrorMessage = errorData.message || errorData.error || 
                          `Server response: ${iconResponse.status} - Authentication failed`;
      } catch (e) {
        serverErrorMessage = `Server response: ${iconResponse.status} - Authentication error`;
      }
      
      // Try to fetch without auth
      let nonAuthResponse;
      try {
        nonAuthResponse = await fetch(`${backendUrl}/asset`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ slug: slug || '' }),
        });
      } catch (fetchError) {
        return {
          props: {
            ...defaultProps,
            authError: true,
            error: `Failed to connect to server without auth: ${fetchError.message}`
          }
        };
      }
      
      if (nonAuthResponse.ok) {
        try {
          iconData = await nonAuthResponse.json();
          // Add the error message to iconData
          iconData.error_message = serverErrorMessage;
        } catch (jsonError) {
          return {
            props: {
              ...defaultProps,
              authError: true,
              error: `Failed to parse server response: ${jsonError.message}`
            }
          };
        }
      } else {
        return {
          props: {
            ...defaultProps,
            authError: true,
            error: `Failed to fetch icon data: ${nonAuthResponse.status}`
          }
        };
      }
    } else if (!iconResponse.ok) {
      return {
        props: {
          ...defaultProps,
          error: `Server responded with ${iconResponse.status}`
        }
      };
    } else {
      // Normal successful response
      try {
        iconData = await iconResponse.json();
      } catch (jsonError) {
        return {
          props: {
            ...defaultProps,
            error: `Failed to parse icon data: ${jsonError.message}`
          }
        };
      }
    }
    
    // Pre-process image URL for SSR
    let imageUrl = '';
    
    // If we have icon data, process the image URL
    if (iconData) {
      // Use url_large if available (especially for basic3d)
      if (iconData.url_large) {
        imageUrl = iconData.url_large;
      } else {
        const iconCollection = iconData.collection || collection;
        switch (iconCollection) {
          case 'basic':
            imageUrl = `https://icons-pbl.davooda.com/basic-icons/${slug}.svg`;
            break;
          case 'outline':
            imageUrl = `https://icons-pbl.davooda.com/outline/png-360px/${slug}-360x360.png`;
            break;
          case 'polish-blue':
            imageUrl = `https://icons-pbl.davooda.com/polish-blue/png-360px/${slug}-360x360.png`;
            break;
          case 'basic3d':
            imageUrl = `https://icons-pbl.davooda.com/basic3d-icons/png-512px/${slug}.png`;
            break;
          default:
            imageUrl = iconData.image_url || '';
        }
      }
      
      // Add the processed image URL to iconData
      iconData.processedImageUrl = imageUrl;
    }
    
    // Fetch similar icons data
    let similarIconsData = { items: [] };
    try {
      const similarIconsResponse = await fetch(`${backendUrl}/similar_icons`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          slug: slug || '', 
          collections: similarCollections || ['basic', 'outline', 'polish-blue', 'basic3d'] 
        }),
      });
      
      if (similarIconsResponse.ok) {
        const responseText = await similarIconsResponse.text();
        
        try {
          const responseData = JSON.parse(responseText);
          
          // Check if response is already properly structured
          if (responseData && typeof responseData === 'object') {
            if (responseData.items && Array.isArray(responseData.items)) {
              // Response is already in the correct format { items: [] }
              similarIconsData = responseData;
            } else if (Array.isArray(responseData)) {
              // Response is an array, wrap it in an object with items property
              similarIconsData = { items: responseData };
            } else {
              // Response is an object but not in expected format
              // Try to find an array property that might contain the icons
              const possibleArrayProps = Object.keys(responseData).filter(key => 
                Array.isArray(responseData[key]) && responseData[key].length > 0
              );
              
              if (possibleArrayProps.length > 0) {
                // Use the first array property found
                const arrayProp = possibleArrayProps[0];
                similarIconsData = { items: responseData[arrayProp] };
              } else {
                // No suitable array found, initialize with empty items
                similarIconsData = { items: [] };
              }
            }
          } else {
            // Response is not an object
            similarIconsData = { items: [] };
          }
        } catch (parseError) {
          // If parsing fails, use empty array
          similarIconsData = { items: [] };
        }
      }
    } catch (similarError) {
      // If fetching fails, use empty array
      similarIconsData = { items: [] };
    }
    
    return {
      props: {
        slug: slug || '',
        collection: collection || 'basic',
        backendUrl,
        iconData: iconData || null,
        imageUrl: imageUrl || '',
        similarIconsData,
        similarCollections,
        authError,
        error: null,
        timestamp: Date.now()
      }
    };
  } catch (error) {
    return {
      props: {
        ...defaultProps,
        error: `Server error: ${error.message}`
      }
    };
  }
}

// Icon detail page component with improved error handling
function IconDetailPage({ 
  slug, 
  collection, 
  backendUrl, 
  iconData, 
  imageUrl,
  similarIconsData,
  similarCollections,
  authError,
  error
}) {
  // Function to reload page (only used on client-side)
  const handleReload = (e) => {
    e?.preventDefault?.();
    if (typeof window !== 'undefined') {
      // Add a timestamp parameter to prevent caching during reload
      const url = new URL(window.location.href);
      url.searchParams.set('reload', Date.now());
      window.location.href = url.toString();
    }
  };

  // Prepare title and description for SEO
  const keywordsArray = iconData?.keywords?.length ? iconData.keywords : [slug || 'icon'];
  const safeKeywords = Array.isArray(keywordsArray) ? keywordsArray : [slug || 'icon'];
  const pageTitle = safeKeywords.slice(0, 3).join(', ') || slug || 'Icon Detail';
  const pageDescription = iconData 
    ? `Beautiful handcrafted ${pageTitle} icon` 
    : `Icon details for ${slug || 'icon'}`;

  return (
    <BaseLayout
      title={`${pageTitle} ${collection === 'basic3d' ? '3D' : 'vector'} icon`}
      description={pageDescription}
      showHeader={true}
    >
      <div className="page-container-with-top-margin" style={{ 
        minHeight: '700px',
        position: 'relative',
      }}>
        {/* Error Display */}
        {error ? (
          <div className="p-4 m-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <h3 className="font-bold text-lg mb-2">Error loading icon</h3>
            <p>{error}</p>
            
            <button 
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              onClick={handleReload}
            >
              Reload page
            </button>
          </div>
        ) : (
          <ErrorBoundary>
            <IconDetail
              slug={slug || ''}
              BACKEND_URL={backendUrl}
              collection={collection || 'basic'}
              initialData={iconData}
              imageUrl={imageUrl || ''}
              similarIconsData={similarIconsData || { items: [] }}
              similarCollections={similarCollections || ['basic', 'outline', 'polish-blue', 'basic3d']}
              authError={!!authError}
            />
          </ErrorBoundary>
        )}
      </div>
    </BaseLayout>
  );
}

export default IconDetailPage;